import React, { useState, useEffect, useCallback } from 'react';
import './3Dpr.css';
import axios from 'axios';

const PRINTERS = [
  { barcode: "3DCALCIFER", title: "Calcifer", type: "Bambu Lab P1S", bgColor: "#f59e24" },
  { barcode: "3DJIJI", title: "Jiji", type: "Bambu Lab P1S", bgColor: "#2c3e50" },
  { barcode: "3DMONONOKE", title: "Mononoke", type: "Bambu Lab P1S", bgColor: "#64b1ff" },
  { barcode: "3DTOTORO", title: "Totoro", type: "Bambu Lab P1S", bgColor: "#a0a57b" },
  { barcode: "3DPONYO", title: "Ponyo", type: "Bambu Lab P2S", bgColor: "#e74c3c" },
  { barcode: "3DARRIETTY", title: "Arrietty", type: "Bambu Lab P2S", bgColor: "#27ae60" },
];

const BARCODE_ENDPOINT_BASE = "https://wco.ra.cs.uchicago.edu/api/barcode/";
const POLL_INTERVAL_MS = 30_000;

const Schedule = () => {
  const [slots, setSlots] = useState(
    PRINTERS.map((p, i) => ({
      id: i,
      title: p.title,
      type: p.type,
      isAvailable: true,
      schedule: [],
      bgColor: p.bgColor,
      barcode: p.barcode,
    }))
  );

  const [dayOffset, setDayOffset] = useState(0);
  const [ready, setReady] = useState(false);

  const fetchPrinterData = useCallback(async () => {
    try {
      const responses = await Promise.all(
        PRINTERS.map(p =>
          axios.get(`${BARCODE_ENDPOINT_BASE}${p.barcode}`).catch(() => null)
        )
      );

      setSlots(prev =>
        prev.map((slot, i) => {
          const res = responses[i];
          if (!res) return slot;
          const data = res.data;
          return {
            ...slot,
            isAvailable: data.currentAllocation === null,
            schedule: (data.activeSchedule || []).map(s => ({
              start: s.startTime,
              end: s.endTime,
            })),
          };
        })
      );
      setReady(true);
    } catch (err) {
      console.error("Failed to fetch printer data:", err);
    }
  }, []);

  useEffect(() => {
    fetchPrinterData();
    const interval = setInterval(fetchPrinterData, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchPrinterData]);

  const getDisplayDate = (offset) => {
    const date = new Date();
    date.setDate(date.getDate() + offset);
    return date;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  };

  const processTimeBlocks = (schedule) => {
    const displayDate = getDisplayDate(dayOffset);
    const timelineStart = new Date(displayDate);
    timelineStart.setHours(10, 0, 0, 0);
    const timelineEnd = new Date(displayDate);
    timelineEnd.setHours(22, 0, 0, 0);
  
    const now = new Date();
  
    return schedule
      .map(entry => {
        const start = new Date(entry.start);
        const end = new Date(entry.end);
        const localStart = new Date(start.toLocaleString());
        const localEnd = new Date(end.toLocaleString());
  
        if (localEnd < timelineStart || localStart > timelineEnd) {
          return null;
        }
  
        const adjustedStart = localStart < timelineStart ? timelineStart : localStart;
        const adjustedEnd = localEnd > timelineEnd ? timelineEnd : localEnd;
  
        const totalHours = 12;
        const startOffset = (adjustedStart - timelineStart) / (1000 * 60 * 60);
        const duration = (adjustedEnd - adjustedStart) / (1000 * 60 * 60);
  
        const isCurrent = now >= adjustedStart && now <= adjustedEnd;
        let currentPosition = null;
  
        if (isCurrent) {
          currentPosition = ((now - adjustedStart) / (adjustedEnd - adjustedStart)) * 100;
        }
  
        return {
          left: (startOffset / totalHours) * 100,
          width: (duration / totalHours) * 100,
          startTime: adjustedStart.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
          endTime: adjustedEnd.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
          isCurrent,
          currentPosition
        };
      })
      .filter(entry => entry !== null && entry.width > 0);
  };

  const generateTimeTickers = () => {
    const ticks = [];
    const displayDate = getDisplayDate(dayOffset);
    for (let hour = 10; hour <= 22; hour++) {
      const time = new Date(displayDate);
      time.setHours(hour, 0, 0, 0);
      let formatted = time.toLocaleTimeString([], { hour: 'numeric', hour12:false});
      ticks.push(formatted);
    }

    return ticks;
  };

  return (
    <div className="main margins">
      <div className="title col" style = {{flexGrow:"0"}}>
        <h4>RESERVATIONS</h4>
        <h1>3D Printer Availability</h1>
      </div>

      <div className="dayNavigation">
        {[0, 1, 2, 3].map(offset => (
          <button
            key={offset}
            className={`dayButton ${dayOffset === offset ? 'active' : ''}`}
            onClick={() => setDayOffset(offset)}
          >
            {formatDate(getDisplayDate(offset))}
          </button>
        ))}
      </div>

      <div className="scheduleContainer">
        {slots.map((slot) => {
          const timeBlocks = processTimeBlocks(slot.schedule);
          
          return (
            <div className="printerTimeCard" key={slot.id}>
              <div className="printerHeader">
                <div>
                  <h3 className="printerTitle">{slot.title}</h3>
                  <span className="printerType">{slot.type}</span>
                </div>
                <div className={(ready && (dayOffset < 1)? "show " : "hidden ") + "availabilityStatus"}>
                  <span style={{ fontSize: "1rem" }}>
                    {slot.isAvailable ? 'Available Now' : 'Occupied'}
                  </span>
                  <div className={`indicator ${slot.isAvailable ? 'available' : 'occupied'}`} />
                </div>
              </div>
              <div className="printerSchedule">
                {!ready ? (
                  <div className="loadingIndicator">
                    <div className="spinner" />
                  </div>
                ) : (
                  <>
                    <div className="timeline">
                      {timeBlocks.length === 0 ? (
                        <div className="noReservations">No reservations</div>
                      ) : (
                        timeBlocks.map((block, i) => ( block.width>.5?
                          <div
                            key={i}
                            className="timeBlock"
                            style={{
                              background: slot.bgColor,
                              left: `${block.left}%`,
                              width: `${block.width}%`,
                            }}
                          >
                            <span className="timeLabel">
                              {block.startTime} -<br/>{block.endTime}
                            </span>
                            {block.isCurrent && (
                              <div
                                className="currentTimeIndicator"
                                style={{
                                  left: `${block.currentPosition}%`,
                                }}
                              ></div>
                            )}
                          </div> : null
                        ))
                      )}
                    </div>
                    <div className="timeLabels">
                      {generateTimeTickers().map((label, index) => (
                        <span className={"timeTick"} key={index}>{label}</span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Schedule;
